import { type FC, useMemo, useState } from "react";
import type { LocationPlatform, UnifiedReviewDto } from "shared";
import { Button } from "../components/Button";
import { SegmentedControl } from "../components/SegmentedControl";
import {
	useUnifiedReviews,
	useUnifiedReviewsActions,
} from "../settings/useIntegrations";
import { UnifiedReviewCard } from "./UnifiedReviewCard";

type PlatformFilter = "all" | Extract<LocationPlatform, "google" | "facebook">;
type RatingFilter = "all" | "5" | "4" | "3" | "low";
type ReplyFilter = "all" | "replied" | "unreplied";

const PLATFORM_OPTIONS: Array<{ value: PlatformFilter; label: string }> = [
	{ value: "all", label: "All" },
	{ value: "google", label: "Google" },
	{ value: "facebook", label: "Facebook" },
];

const RATING_OPTIONS: Array<{ value: RatingFilter; label: string }> = [
	{ value: "all", label: "Any stars" },
	{ value: "5", label: "5★" },
	{ value: "4", label: "4★" },
	{ value: "3", label: "3★" },
	{ value: "low", label: "1–2★" },
];

const REPLY_OPTIONS: Array<{ value: ReplyFilter; label: string }> = [
	{ value: "all", label: "All replies" },
	{ value: "unreplied", label: "Needs reply" },
	{ value: "replied", label: "Replied" },
];

const STAR_VALUE: Record<UnifiedReviewDto["starRating"], number> = {
	ONE: 1,
	TWO: 2,
	THREE: 3,
	FOUR: 4,
	FIVE: 5,
	NONE: 0,
};

function hasReply(review: UnifiedReviewDto) {
	return !!review.reviewReply?.comment?.trim();
}

function matchesRating(review: UnifiedReviewDto, filter: RatingFilter) {
	if (filter === "all") return true;
	const stars = STAR_VALUE[review.starRating];
	if (filter === "low") return stars === 1 || stars === 2;
	return stars === Number(filter);
}

function matchesReply(review: UnifiedReviewDto, filter: ReplyFilter) {
	if (filter === "all") return true;
	const replied = hasReply(review);
	return filter === "replied" ? replied : !replied;
}

type Props = { locationId?: string };

export const BusinessReviews: FC<Props> = ({ locationId }) => {
	const reviews = useUnifiedReviews(locationId);
	const { refresh } = useUnifiedReviewsActions(locationId);
	const [platform, setPlatform] = useState<PlatformFilter>("all");
	const [rating, setRating] = useState<RatingFilter>("all");
	const [reply, setReply] = useState<ReplyFilter>("all");

	const filtered = useMemo(() => {
		return reviews.reviews.filter((review) => {
			if (platform !== "all" && review.platform !== platform) return false;
			if (!matchesRating(review, rating)) return false;
			if (!matchesReply(review, reply)) return false;
			return true;
		});
	}, [platform, rating, reply, reviews.reviews]);

	const counts = useMemo(() => {
		let google = 0;
		let facebook = 0;
		let unreplied = 0;
		for (const review of reviews.reviews) {
			if (review.platform === "google") google += 1;
			if (review.platform === "facebook") facebook += 1;
			if (!hasReply(review)) unreplied += 1;
		}
		return {
			all: reviews.reviews.length,
			google,
			facebook,
			unreplied,
		};
	}, [reviews.reviews]);

	const platformOptions = PLATFORM_OPTIONS.map((option) => ({
		value: option.value,
		label: `${option.label} (${counts[option.value]})`,
	}));

	const emptyMessage =
		platform === "all" && rating === "all" && reply === "all"
			? "No reviews yet. Connect Google Business and Facebook, then refresh."
			: "No reviews match these filters.";

	return (
		<div className="business-reviews">
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 16,
					marginBottom: 16,
					flexWrap: "wrap",
				}}
			>
				<div>
					<h2 style={{ marginBottom: 4 }}>Reviews</h2>
					<p
						style={{
							margin: 0,
							color: "var(--bs-secondary-color)",
							fontSize: 14,
						}}
					>
						{filtered.length}
						{filtered.length !== reviews.totalReviews
							? ` of ${reviews.totalReviews}`
							: ""}{" "}
						reviews
						{reviews.averageRating > 0
							? ` · ${reviews.averageRating.toFixed(1)} avg`
							: ""}
						{counts.unreplied > 0 ? ` · ${counts.unreplied} need a reply` : ""}
					</p>
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						flexWrap: "wrap",
					}}
				>
					<SegmentedControl
						ariaLabel="Filter reviews by platform"
						options={platformOptions}
						value={platform}
						onChange={setPlatform}
					/>
					<select
						aria-label="Filter by rating"
						className="form-select form-select-sm"
						style={{ width: "auto" }}
						value={rating}
						onChange={(e) => setRating(e.target.value as RatingFilter)}
					>
						{RATING_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<select
						aria-label="Filter by reply status"
						className="form-select form-select-sm"
						style={{ width: "auto" }}
						value={reply}
						onChange={(e) => setReply(e.target.value as ReplyFilter)}
					>
						{REPLY_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<Button
						variant="ghost"
						size="sm"
						icon="sync"
						loading={refresh.isPending}
						onClick={() => refresh.mutate()}
					>
						Refresh
					</Button>
				</div>
			</div>

			{filtered.length === 0 ? (
				<p style={{ color: "var(--bs-secondary-color)" }}>{emptyMessage}</p>
			) : (
				filtered.map((review) => (
					<UnifiedReviewCard
						key={review.id}
						review={review}
						locationId={locationId}
					/>
				))
			)}
		</div>
	);
};
